import { addDays, formatShortDate, monthName } from '@app/shared'
import type { ISODate } from '@app/shared'

import type { LearningPace, LearningPacePoint } from '../../lib/data'

// ============================================================================
// THE PACE CHART — docs/design/handoff/"LifeOS Learning.dc.html", option 9b.
//
// The one chart that matters on this screen: cumulative items finished against the plan line, over
// the whole six months. Am I on the line or off it.
//
// THE PLAN LINE IS STRAIGHT because the plan is one item a day; it is drawn dashed and grey so it
// reads as an expectation rather than a result. The teal line stops at today and does not
// extrapolate — a projected finish date is a promise this app has no business making.
//
// The y-axis is the plan's own length, so the chart cannot be read as "nearly there" when it is a
// quarter of the way in. Every number below comes from `paceSeries`, which the data contract
// derives from the same completions the session log lists.
// ============================================================================

const W = 418
const H = 178
const LEFT = 34
const RIGHT = 410
const TOP = 8
const BASE = 152

export function PaceChart({
  series,
  pace,
  total,
  planStart,
  planEnd,
  today,
}: {
  series: LearningPacePoint[]
  pace: LearningPace
  total: number
  planStart: ISODate
  planEnd: ISODate
  today: ISODate
}) {
  const span = Math.max(1, series.length - 1)
  // The x-axis is the WHOLE plan, not just the part that has happened — which is what makes the
  // teal line visibly short and the gap ahead of it visibly long.
  const planDays = Math.max(1, total - 1)
  const x = (i: number) => LEFT + (i / planDays) * (RIGHT - LEFT)
  const y = (n: number) => BASE - (n / total) * (BASE - TOP)

  const path = series.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(p.done)}`).join(' ')
  const last = series[series.length - 1]
  const todayX = x(span)
  const todayY = last ? y(last.done) : BASE

  const ticks = [total, Math.round(total * 0.75), Math.round(total * 0.5), Math.round(total * 0.25), 0]
  // Two month markers a third and two thirds of the way along — the plan's own calendar, read off
  // its start and end rather than guessed.
  const mid1 = addDays(planStart, Math.round(planDays / 3))
  const mid2 = addDays(planStart, Math.round((planDays * 2) / 3))

  return (
    <section className="flex flex-none flex-col gap-[13px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex items-center gap-[10px]">
        <h2 className="text-[12.5px] font-semibold leading-none">Pace</h2>
        <span className="text-micro font-normal text-microcopy">items done vs. plan</span>
        <div className="ml-auto flex items-center gap-[10px]" aria-hidden>
          <span className="flex items-center gap-[5px]">
            <span className="h-[2.5px] w-[14px] rounded-[2px] bg-learning" />
            <span className="text-[10px] font-medium leading-none text-ink-3">you</span>
          </span>
          <span className="flex items-center gap-[5px]">
            <span className="w-[14px] border-t-2 border-dashed border-faintest-2" />
            <span className="text-[10px] font-medium leading-none text-ink-3">plan</span>
          </span>
        </div>
      </header>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-[178px] w-full"
        role="img"
        aria-label={`${last?.done ?? 0} of ${total} items finished, ${pace.phrase}.`}
      >
        {ticks.map((tick, i) => (
          <g key={tick}>
            <line
              x1={LEFT}
              y1={y(tick)}
              x2={RIGHT}
              y2={y(tick)}
              strokeWidth="1"
              className={i === ticks.length - 1 ? 'stroke-line' : 'stroke-fill'}
            />
            <text
              x={LEFT - 6}
              y={y(tick) + 4}
              textAnchor="end"
              className="fill-microcopy text-[9px]"
              style={{ fontFamily: 'inherit' }}
            >
              {tick}
            </text>
          </g>
        ))}

        {/* The plan: one item a day, start to finish. */}
        <line
          x1={LEFT}
          y1={BASE}
          x2={RIGHT}
          y2={TOP}
          strokeWidth="2"
          strokeDasharray="4 4"
          strokeLinecap="round"
          className="stroke-faintest-2"
        />

        <path d={`${path} L${todayX} ${BASE} L${LEFT} ${BASE} Z`} className="fill-learning opacity-[0.07]" />
        <path
          d={path}
          fill="none"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="stroke-learning"
        />

        <line
          x1={todayX}
          y1={TOP}
          x2={todayX}
          y2={BASE}
          strokeWidth="1"
          strokeDasharray="3 3"
          className="stroke-dash"
        />
        <circle cx={todayX} cy={todayY} r="7.5" fill="none" strokeWidth="1.4" className="stroke-learning opacity-[0.35]" />
        <circle cx={todayX} cy={todayY} r="4" className="fill-learning" />
        <text
          x={todayX + 6}
          y={todayY - 6}
          className="fill-teal text-[9.5px] font-semibold"
          style={{ fontFamily: 'inherit' }}
        >
          {last?.done ?? 0} done · {pace.phrase}
        </text>

        <text x={LEFT} y={170} className="fill-microcopy text-[9px]" style={{ fontFamily: 'inherit' }}>
          {formatShortDate(planStart)}
        </text>
        <text
          x={todayX}
          y={170}
          textAnchor="middle"
          className="fill-teal text-[9px] font-semibold"
          style={{ fontFamily: 'inherit' }}
        >
          today
        </text>
        <text x={x(planDays / 3)} y={170} textAnchor="middle" className="fill-microcopy text-[9px]" style={{ fontFamily: 'inherit' }}>
          {monthName(mid1, true)}
        </text>
        <text x={x((planDays * 2) / 3)} y={170} textAnchor="middle" className="fill-microcopy text-[9px]" style={{ fontFamily: 'inherit' }}>
          {monthName(mid2, true)}
        </text>
        <text x={RIGHT} y={170} textAnchor="end" className="fill-microcopy text-[9px]" style={{ fontFamily: 'inherit' }}>
          {formatShortDate(planEnd)}
        </text>
        <title>{`${last?.done ?? 0} of ${total} items by ${today}, ${pace.phrase}.`}</title>
      </svg>
    </section>
  )
}
